import { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { Lead, Company, User } from '../types';
import { buildLeadSearchFilter } from '../lib/leadSearch';
import { statusesDoDesfecho } from '../lib/desfecho';
import { copyLeadRelatedData } from '../utils/leadUtils';
import { chunk, normalizeEmail, phoneLikePattern, phoneSuffixKey } from '../lib/leadImport';

// Camada de serviço do módulo de Leads: acesso à tabela `leads` (a mais
// usada do sistema) e à contagem de mensagens de WhatsApp não lidas que
// aparece junto com cada lead na lista.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface FetchLeadsPageParams {
  user: { id: string; role?: string; user_metadata?: { company_id?: number; role?: string } } | null | undefined;
  isSuperAdmin: boolean;
  permissions: string[];
  targetCompanyId: number | null | undefined;
  targetFunnelId?: number | null;
  limit?: number;
  statusFilter?: string;
  searchTerm?: string;
  turmaId?: string | null;
  targetUserId?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  stages?: any[];
  pageParam?: string | null;
  /** Exportação: dispensa mensagens de WhatsApp (não saem na planilha e pesam muito por página). */
  skipMessages?: boolean;
}

export interface LeadsPageResult {
  data: Lead[];
  nextCursor: string | null;
}

// O PostgREST corta respostas em 1000 linhas; sem paginar, o Kanban mostrava só os
// 1000 leads mais novos do funil (colunas com contagem menor que a real).
const LEADS_PAGE_SIZE = 1000;

export async function fetchLeadsPage(params: FetchLeadsPageParams): Promise<LeadsPageResult> {
  const { user, isSuperAdmin, permissions, targetCompanyId, targetFunnelId, limit = LEADS_PAGE_SIZE, statusFilter, searchTerm, turmaId, targetUserId, stages, pageParam, skipMessages } = params;

  if (!user || !supabase || !targetCompanyId) return { data: [], nextCursor: null };

  let query = supabase
    .from('leads')
    .select(`
                *,
                users!leads_user_id_fkey(name),
                lead_tags (tags (*)),
                lead_tasks (id, status, due_date)
            `)
    .eq('company_id', targetCompanyId)
    .order('created_at', { ascending: false });

  if (limit) {
    query = query.limit(limit);
  }

  if (pageParam) {
    query = query.lt('created_at', pageParam);
  }

  if (targetFunnelId) query = query.eq('funnel_id', targetFunnelId);

  const searchFilter = buildLeadSearchFilter(searchTerm);
  if (searchFilter) query = query.or(searchFilter);

  if (statusFilter) {
    const wonStatuses = statusesDoDesfecho(stages || [], 'won');
    const lostStatuses = statusesDoDesfecho(stages || [], 'lost');

    if (statusFilter === 'active') {
      query = query.not('status', 'in', `(${[...wonStatuses, ...lostStatuses].join(',')})`);
    }
    else if (statusFilter === 'won') query = query.in('status', wonStatuses);
    else if (statusFilter === 'lost') query = query.in('status', lostStatuses);
  }

  if (turmaId) {
    if (turmaId === 'unallocated') query = query.is('turma_id', null);
    else if (turmaId !== 'all') query = query.eq('turma_id', turmaId);
  }

  // Resgata a role do usuário considerando os metadados
  const userRole = user?.role ?? user?.user_metadata?.role;
  // Quem tem leads.view_all pode filtrar por qualquer vendedor no dropdown — é a
  // mesma regra que o RLS já aplica na leitura, então front e banco ficam
  // consistentes (antes, um vendedor numa empresa sem restrição já via tudo via
  // RLS mas não tinha o filtro "ver de fulano" liberado aqui).
  const isAdminOrManager = isSuperAdmin || permissions.includes('leads.view_all');

  if (isAdminOrManager) {
    if (targetUserId && targetUserId !== 'all') {
      // "Dono do Lead" é o responsável ATUAL (campo user_id, atualizado a
      // cada transferência) — sdr_id/closer_id são só o histórico de quem
      // qualificou/fechou e continuam preenchidos mesmo depois do lead
      // trocar de dono, então não podem entrar nesse filtro.
      if (targetUserId === 'unassigned') query = query.is('user_id', null);
      else query = query.eq('user_id', targetUserId);
    }
  } else if (userRole === 'vendedor') {
    // Permite ao vendedor ver os leads vinculados a ele (como responsável, SDR ou Closer)
    query = query.or(`user_id.eq.${user.id},sdr_id.eq.${user.id},closer_id.eq.${user.id}`);
  } else if (userRole === 'sdr') {
    // Permite ao SDR ver os leads vinculados a ele (como responsável ou SDR)
    query = query.or(`user_id.eq.${user.id},sdr_id.eq.${user.id}`);
  } else if (userRole !== 'operacao' && userRole !== 'cs') {
    query = query.eq('user_id', user.id);
  }

  const { data: pageData, error: pageError } = await query;
  if (pageError) throw pageError;

  const fetchedLeads = pageData || [];
  const nextCursor = (limit && fetchedLeads.length === limit) ? fetchedLeads[fetchedLeads.length - 1].created_at : null;

  if (fetchedLeads.length === 0) return { data: [], nextCursor: null };

  if (skipMessages) {
    const light = fetchedLeads.map((lead: Record<string, unknown>) => ({
      ...lead,
      tags: (lead.lead_tags as Array<Record<string, unknown>>)?.map(lt => lt.tags) || [],
    }));
    return { data: light as Lead[], nextCursor };
  }

  // Última mensagem, não lidas e última não lida de cada lead, numa chamada só (uma linha por lead).
  // Antes eram ~10 consultas de até 1000 mensagens por página + todas as não lidas da empresa,
  // repetidas a cada mudança em tempo real: isso esgotou a memória do banco em 03/11/2026.
  const previewText = (body?: string | null, type?: string | null) => {
    if (type === 'image') return '📷 Imagem';
    if (type === 'video') return '🎥 Vídeo';
    if (type === 'audio') return '🎵 Áudio';
    if (type === 'document') return '📄 Documento';
    return body || '';
  };
  const leadIds = fetchedLeads.map((l: Record<string, unknown>) => l.id as number);
  const { data: previews, error: previewsError } = await supabase.rpc('lead_message_previews', {
    p_company_id: targetCompanyId,
    p_lead_ids: leadIds,
  });
  if (previewsError) console.warn('Prévia de mensagens indisponível:', previewsError.message);
  const byLead = new Map<number, LeadMessagePreview>();
  for (const p of (previews || []) as LeadMessagePreview[]) byLead.set(Number(p.lead_id), p);

  const formattedLeads = fetchedLeads.map((lead: Record<string, unknown>) => ({
    ...lead,
    tags: (lead.lead_tags as Array<Record<string, unknown>>)?.map(lt => lt.tags) || [],
    ...messagePreviewFields(byLead.get(lead.id as number), previewText),
  }));

  return { data: formattedLeads as Lead[], nextCursor };
}

interface LeadMessagePreview {
  lead_id: number;
  last_body: string | null;
  last_type: string | null;
  last_direction: string | null;
  unread_count: number;
  last_unread_body: string | null;
  last_unread_type: string | null;
}

function messagePreviewFields(
  p: LeadMessagePreview | undefined,
  previewText: (body?: string | null, type?: string | null) => string,
) {
  if (!p) return { unread_messages_count: 0, last_unread_message: undefined, last_message: undefined, last_message_direction: undefined };
  const hasLast = p.last_body != null || p.last_type != null;
  return {
    unread_messages_count: p.unread_count || 0,
    last_unread_message: p.unread_count > 0 ? previewText(p.last_unread_body, p.last_unread_type) || undefined : undefined,
    last_message: hasLast ? previewText(p.last_body, p.last_type) || undefined : undefined,
    last_message_direction: hasLast ? p.last_direction || undefined : undefined,
  };
}

export const EXPORT_MAX_LEADS = 50000;

/**
 * Todos os leads que batem com os filtros do servidor (funil, status, busca, turma, responsável),
 * página a página — a lista da tela é paginada e só carrega o que o usuário rolou.
 */
export async function fetchAllLeadsForExport(
  params: Omit<FetchLeadsPageParams, 'limit' | 'pageParam' | 'skipMessages'>,
  onProgress?: (loaded: number) => void,
): Promise<{ leads: Lead[]; truncated: boolean }> {
  const all: Lead[] = [];
  let cursor: string | null = null;
  while (all.length < EXPORT_MAX_LEADS) {
    const page: LeadsPageResult = await fetchLeadsPage({ ...params, limit: 1000, pageParam: cursor, skipMessages: true });
    all.push(...page.data);
    onProgress?.(all.length);
    if (!page.nextCursor) return { leads: all, truncated: false };
    cursor = page.nextCursor;
  }
  return { leads: all, truncated: true };
}

/**
 * Leads escolhidos pelo usuário (exportação dos selecionados), mesmo os que já saíram da lista carregada.
 * As regras de acesso (RLS) valem como em qualquer leitura: o que a pessoa não pode ver simplesmente não volta.
 */
export async function fetchLeadsByIdsForExport(companyId: number, ids: number[]): Promise<Lead[]> {
  if (!supabase || ids.length === 0) return [];
  const CHUNK = 200;
  const out: Lead[] = [];
  for (let i = 0; i < ids.length; i += CHUNK) {
    const { data, error } = await supabase
      .from('leads')
      .select(`
        *,
        users!leads_user_id_fkey(name),
        lead_tags (tags (*))
      `)
      .eq('company_id', companyId)
      .in('id', ids.slice(i, i + CHUNK));
    if (error) throw error;
    (data || []).forEach((lead: Record<string, unknown>) => out.push({
      ...lead,
      tags: (lead.lead_tags as Array<Record<string, unknown>>)?.map(lt => lt.tags) || [],
    } as unknown as Lead));
  }
  return out;
}

export async function updateLeadStatus(leadId: number, newStatus: string): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase.from('leads').update({ status: newStatus, updated_at: new Date().toISOString() }).eq('id', leadId);
  if (error) throw error;
}

export async function fetchLeadStatus(leadId: number): Promise<string | null> {
  if (!supabase) return null;
  const { data } = await supabase.from('leads').select('status').eq('id', leadId).maybeSingle();
  return data?.status ?? null;
}

export async function updateLeadValue(leadId: number, newValue: number): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase.from('leads').update({ value: newValue, updated_at: new Date().toISOString() }).eq('id', leadId);
  if (error) throw error;
}

export async function markLeadAsLost(leadId: number, lostStage: string | undefined, notes: string): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase
    .from('leads')
    .update({
      status: 'lost',
      lost_stage: lostStage,
      notes,
      updated_at: new Date().toISOString()
    })
    .eq('id', leadId);
  if (error) throw error;
}

export async function deleteLead(leadId: number): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase.from('leads').delete().eq('id', leadId);
  if (error) throw error;
}

export async function fetchAllCompanyUsers(companyId: number): Promise<User[]> {
  if (!supabase) return [];
  const { data, error } = await supabase.from('users').select('*').eq('company_id', companyId).order('name');
  if (error) throw error;
  return data || [];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateLeadProposalData(leadId: number, status: string, value: number, metadata: Record<string, any>): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase
    .from('leads')
    .update({ status, value, metadata, updated_at: new Date().toISOString() })
    .eq('id', leadId);
  if (error) throw error;
}

export interface AssignCloserParams {
  leadId: number;
  status: string;
  closerId: string;
  sdrId?: string | null;
  notes?: string | null;
}

export async function assignCloserToLead({ leadId, status, closerId, sdrId, notes }: AssignCloserParams): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase
    .from('leads')
    .update({
      status,
      closer_id: closerId,
      user_id: closerId, // Transferir responsabilidade
      sdr_id: sdrId,
      updated_at: new Date().toISOString(),
      notes
    })
    .eq('id', leadId);
  if (error) throw error;
}

export async function bulkMoveLeadsToStage(leadIds: number[], funnelId: number, status: string): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase
    .from('leads')
    .update({ funnel_id: funnelId, status, updated_at: new Date().toISOString() })
    .in('id', leadIds);
  if (error) throw error;
}

export async function bulkTransferLeadOwner(leadIds: number[], newUserId: string): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase
    .from('leads')
    .update({ user_id: newUserId, updated_at: new Date().toISOString() })
    .in('id', leadIds);
  if (error) throw error;
}

export async function bulkDeleteLeads(leadIds: number[]): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase.from('leads').delete().in('id', leadIds);
  if (error) throw error;
}

// Assina mudanças em tempo real na tabela `leads` e em mensagens de
// WhatsApp da empresa, disparando `onChange` (tipicamente um refetch do
// React Query) sempre que algo muda. Retorna a função de cancelamento.
export const LEADS_REALTIME_DEBOUNCE_MS = 5000;

export function subscribeToLeadsChanges(companyId: number, onChange: () => void): () => void {
  if (!supabase) return () => {};
  const client: SupabaseClient = supabase;

  // Junta rajadas de eventos num único refetch: cada refetch recarrega TODAS as páginas do funil,
  // e sem isso cada mensagem de WhatsApp da empresa recarregava tudo em cada aba aberta.
  let timer: ReturnType<typeof setTimeout> | null = null;
  const trigger = () => {
    if (timer) return;
    timer = setTimeout(() => { timer = null; onChange(); }, LEADS_REALTIME_DEBOUNCE_MS);
  };

  const channel = client
    .channel(`leads_changes_${companyId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'leads', filter: `company_id=eq.${companyId}` },
      trigger
    )
    .subscribe();

  const messagesChannel = client
    .channel(`leads_messages_changes_${companyId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'lead_whatsapp_messages', filter: `company_id=eq.${companyId}` },
      trigger
    )
    .subscribe();

  return () => {
    if (timer) clearTimeout(timer);
    client.removeChannel(channel);
    client.removeChannel(messagesChannel);
  };
}

export async function fetchLeadById(leadId: number): Promise<Lead | null> {
  if (!supabase) return null;
  const { data } = await supabase.from('leads').select('*').eq('id', leadId).maybeSingle();
  return data as Lead | null;
}

export async function countLeadAttachments(leadId: number): Promise<number> {
  if (!supabase) return 0;
  const { count } = await supabase
    .from('lead_attachments')
    .select('*', { count: 'exact', head: true })
    .eq('lead_id', leadId);
  return count || 0;
}

// Empresas visíveis para um superadmin na tela de Leads: todas, ou só a
// própria "matriz" e suas filiais quando o superadmin é restrito a um grupo.
export async function fetchCompaniesForSuperadmin(masterCompanyId: number | undefined, isParentSuperAdmin: boolean, isGlobalSuperAdmin: boolean): Promise<Company[]> {
  if (!supabase) return [];
  let query = supabase.from('companies').select('*').order('name');
  if (isParentSuperAdmin && !isGlobalSuperAdmin && masterCompanyId) {
    query = query.or(`parent_id.eq.${masterCompanyId},id.eq.${masterCompanyId}`);
  }
  const { data } = await query;
  return data || [];
}

// Usuários de uma empresa para popular o filtro "Dono do Lead".
export async function fetchCompanyUsersBasic(companyId: number): Promise<User[]> {
  if (!supabase) return [];
  const { data } = await supabase
    .from('users')
    .select('id, name, email, role, company_id')
    .eq('company_id', companyId)
    .order('name');
  return (data as User[]) || [];
}

// Quantos leads cada responsável (user_id atual) tem na empresa/funil — usado pra
// listar no filtro de responsável só quem realmente tem lead. A chave "unassigned"
// conta os leads sem dono. Pagina em lotes porque o PostgREST limita 1000 linhas.
export async function fetchLeadOwnerCounts(companyId: number, funnelId?: number | null): Promise<Record<string, number>> {
  if (!supabase) return {};
  const PAGE = 1000;
  const MAX_PAGES = 50;
  const counts: Record<string, number> = {};
  for (let page = 0; page < MAX_PAGES; page++) {
    let query = supabase
      .from('leads')
      .select('id, user_id')
      .eq('company_id', companyId)
      .order('id')
      .range(page * PAGE, page * PAGE + PAGE - 1);
    if (funnelId) query = query.eq('funnel_id', funnelId);
    const { data, error } = await query;
    if (error) throw error;
    for (const row of data || []) {
      const key = row.user_id ?? 'unassigned';
      counts[key] = (counts[key] || 0) + 1;
    }
    if (!data || data.length < PAGE) break;
  }
  return counts;
}

export async function fetchFunnelName(funnelId: number): Promise<string | null> {
  if (!supabase) return null;
  const { data } = await supabase.from('funnels').select('name').eq('id', funnelId).single();
  return data ? data.name.toLowerCase() : null;
}

// O status de um lead ativo é o id (numérico) ou o original_id (string) de um
// estágio de funil — nunca os dois ao mesmo tempo, daí a checagem de formato.
export async function fetchStageNameForStatus(status: string): Promise<string | null> {
  if (!supabase) return null;
  const isNumericId = !isNaN(Number(status));
  const query = isNumericId
    ? supabase.from('funnel_stages').select('name').eq('id', Number(status))
    : supabase.from('funnel_stages').select('name').eq('original_id', status);

  const { data } = await query.limit(1).maybeSingle();
  return data ? data.name.toLowerCase() : null;
}

// Primeiro estágio (menor order_index) de um funil, no formato usado como
// `status` do lead (original_id quando existir, senão o id numérico como
// string). Usado tanto para o status inicial de um lead novo quanto para
// devolver um lead reaberto à primeira etapa.
export async function fetchFirstFunnelStageId(funnelId: number): Promise<string | null> {
  if (!supabase) return null;
  const { data: stages } = await supabase
    .from('funnel_stages')
    .select('id, original_id')
    .eq('funnel_id', funnelId)
    .order('order_index', { ascending: true })
    .limit(1);

  if (stages && stages.length > 0) {
    return stages[0].original_id || stages[0].id.toString();
  }
  return null;
}

export async function revertLeadFromLost(leadId: number, revertToStatus: string): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase
    .from('leads')
    .update({
      status: revertToStatus,
      lost_stage: null,
      updated_at: new Date().toISOString()
    })
    .eq('id', leadId);
  if (error) throw error;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateLeadMetadata(leadId: number, metadata: Record<string, any>): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase.from('leads').update({ metadata }).eq('id', leadId);
  if (error) throw error;
}

export interface NewBudgetFromLeadParams {
  /** Lead (ganho) de onde o novo orçamento parte. Ele não é alterado, a não ser pela marca `novo_orcamento_lead_id`. */
  lead: Lead;
  /** Venda mais recente do lead, guardada como referência do orçamento anterior. */
  saleId?: number | null;
  copiarPortas: boolean;
}

// Chaves de metadata que pertencem à venda/ciclo anterior e não vão para o orçamento novo.
const SALE_CYCLE_METADATA_KEYS = [
  'venda_ganho_id', 'etapa_antes_ganho', 'lead_producao_id', 'ciclo_iniciado_em', 'historico_compras',
  'condicao_pagamento', 'forma_pagamento_contrato', 'sinal_proposto', 'parcelas_propostas',
  'down_payment', 'installments', 'next_due_date', 'sale_date', 'payment_method',
  'novo_orcamento_lead_id', 'orcamento_anterior_lead_id'
];

// Abre um novo orçamento para um cliente que já comprou clonando o lead: o lead antigo continua
// no estágio de ganho, com a venda e o card de Produção (lead_producao_id) intactos, e o orçamento
// novo nasce como outro lead na primeira etapa do funil. Devolve o id do lead novo.
export async function createNewBudgetFromLead({ lead, saleId, copiarPortas }: NewBudgetFromLeadParams): Promise<number> {
  if (!supabase) throw new Error('Supabase not initialized');

  const status = (lead.funnel_id ? await fetchFirstFunnelStageId(lead.funnel_id) : null) || 'new';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const novoMeta: Record<string, any> = { ...(lead.metadata || {}) };
  SALE_CYCLE_METADATA_KEYS.forEach(k => delete novoMeta[k]);
  novoMeta.acm_doors = copiarPortas ? (novoMeta.acm_doors || []) : [];
  novoMeta.orcamento_anterior_lead_id = lead.id;
  if (saleId != null) novoMeta.orcamento_anterior_sale_id = saleId;

  const { data, error } = await supabase
    .from('leads')
    .insert({
      company_id: lead.company_id,
      user_id: lead.user_id,
      closer_id: lead.closer_id ?? null,
      sdr_id: lead.sdr_id ?? null,
      funnel_id: lead.funnel_id ?? null,
      status,
      value: 0,
      name: lead.name,
      email: lead.email,
      phone: lead.phone ?? null,
      company_name: lead.company_name ?? null,
      document_number: lead.document_number ?? null,
      address: lead.address ?? null,
      position: lead.position ?? null,
      source: lead.source ?? null,
      notes: lead.notes ?? null,
      metadata: novoMeta
    })
    .select('id')
    .single();
  if (error) throw error;
  const novoLeadId = data.id as number;

  // Anotações do cliente acompanham o orçamento novo (falha aqui nunca derruba a criação)
  await copyLeadRelatedData(supabase, lead.company_id, lead.id, novoLeadId);

  // Marca o lead antigo: o botão "+ Novo orçamento" deixa de aparecer e dá para abrir o orçamento novo
  const { error: marcaError } = await supabase
    .from('leads')
    .update({ metadata: { ...(lead.metadata || {}), novo_orcamento_lead_id: novoLeadId } })
    .eq('id', lead.id);
  if (marcaError) console.error('Erro ao marcar o lead antigo com o novo orçamento:', marcaError);

  return novoLeadId;
}

/**
 * Lead da empresa com o mesmo telefone, em qualquer formatação (máscara, +55, sem o 9). Compara os 8
 * últimos dígitos, igual aos webhooks; havendo mais de um, devolve o mais antigo.
 */
async function findLeadByPhoneSuffix(companyId: number, phone: string): Promise<{ id: number; name: string; notes: string | null } | null> {
    if (!supabase) return null;
    const key = phoneSuffixKey(phone);
    if (!key) return null;
    const { data } = await supabase
        .from('leads')
        .select('id, name, notes, phone')
        .eq('company_id', companyId)
        .like('phone', phoneLikePattern(key))
        .order('id', { ascending: true });
    const match = (data || []).find(l => phoneSuffixKey(l.phone) === key);
    return match ? { id: match.id, name: match.name, notes: match.notes ?? null } : null;
}

export async function findExistingLeadByPhoneOrEmail(companyId: number, phone?: string, email?: string): Promise<{ id: number; name: string } | null> {
  if (!supabase) return null;
  if (phone) {
    const found = await findLeadByPhoneSuffix(companyId, phone);
    if (found) return { id: found.id, name: found.name };
  }
  if (email) {
    const { data } = await supabase
      .from('leads')
      .select('id, name')
      .eq('company_id', companyId)
      .eq('email', email)
      .limit(1)
      .maybeSingle();
    if (data) return data;
  }
  return null;
}

export interface CreateLeadParams {
  companyId: number;
  userId: string;
  funnelId?: number | null;
  status: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formData: Record<string, any>;
}

// Lead criado à mão fica com quem criou: o RPC pula o rodízio do funil nesse INSERT
// (migration 20261104210000). Funil com atribuição direta continua valendo.
export async function createLead({ companyId, userId, funnelId, status, formData }: CreateLeadParams): Promise<number | null> {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc('criar_lead_manual', {
    p_lead: {
      ...formData,
      company_id: companyId,
      user_id: userId,
      funnel_id: funnelId,
      status
    }
  });
  if (error) throw leadInsertError(error);
  return (data as number | null | undefined) ?? null;
}

// 23505 = violação de unicidade. Hoje só a trava de telefone por funil (FDM) faz isso em leads;
// sem este tratamento o usuário via "duplicate key value violates unique constraint ...".
export function leadInsertError(error: { code?: string; message: string }): Error {
  if (error.code === '23505') return new Error('Já existe um lead com esse telefone neste funil. Pesquise pelo telefone para abrir o cadastro existente.');
  return error as unknown as Error;
}

// Playbook ligado à etapa em que o lead está (o mesmo que dispara ao mover o lead para ela).
export async function fetchStagePlaybookId(funnelId: number | null | undefined, status: string): Promise<number | null> {
  if (!supabase || !funnelId || !status) return null;
  const isNumericId = !isNaN(Number(status));
  const query = supabase.from('funnel_stages').select('automation_task_template_id').eq('funnel_id', funnelId);
  const { data } = await (isNumericId ? query.eq('id', Number(status)) : query.eq('original_id', status)).limit(1).maybeSingle();
  return (data?.automation_task_template_id as number | null | undefined) ?? null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateLeadFields(leadId: number, formData: Record<string, any>): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase
    .from('leads')
    .update({
      ...formData,
      updated_at: new Date().toISOString()
    })
    .eq('id', leadId);
  if (error) throw error;
}

// Anotações de atendimento (coluna leads.service_notes). Não mexe em updated_at de
// propósito: salvar anotação não deve "reordenar" o lead por Recém Atualizados.
export async function fetchLeadServiceNotes(leadId: number): Promise<string> {
  if (!supabase) return '';
  const { data, error } = await supabase.from('leads').select('service_notes').eq('id', leadId).maybeSingle();
  if (error) throw error;
  return data?.service_notes ?? '';
}

export async function updateLeadServiceNotes(leadId: number, text: string): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase.from('leads').update({ service_notes: text }).eq('id', leadId);
  if (error) throw error;
}

export async function fetchTurmaById(turmaId: number | string) {
  if (!supabase) return null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data } = await supabase.from('turmas').select('*').eq('id', turmaId).maybeSingle();
  return data;
}

export interface AutoWinSaleParams {
  companyId: number;
  userId: string;
  leadId: number;
  value: number;
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
}

// Cria a venda "silenciosa" quando um lead com valor já preenchido é
// arrastado direto para uma etapa de ganho (sem passar pelo WinLeadModal).
export async function createAutoWinSale(params: AutoWinSaleParams): Promise<void> {
  if (!supabase) throw new Error('Supabase not initialized');
  const { error } = await supabase.from('sales').insert({
    company_id: params.companyId,
    user_id: params.userId,
    product_name: 'Venda Fechada', // Default generic name since it wasn't prompted
    value: params.value,
    sale_date: new Date().toISOString(),
    customer_name: params.customerName,
    customer_email: params.customerEmail,
    customer_phone: params.customerPhone,
    lead_id: params.leadId
  });
  if (error) throw error;
}

// Usado pelo BulkImportLeadsModal para checar duplicidade de telefone/email
// na empresa inteira (não só no funil de destino) antes de importar.
/**
 * Telefones e e-mails que já existem na empresa. Os conjuntos voltam normalizados (telefone pelos 8
 * últimos dígitos, e-mail em minúsculas), então "(11) 99999-9999" e "+55 11999999999" contam como o mesmo.
 * A consulta vai em blocos para não estourar o tamanho da URL em planilhas grandes.
 */
export async function checkExistingPhonesEmails(companyId: number, phones: string[], emails: string[]): Promise<{ existingPhones: Set<string>; existingEmails: Set<string> }> {
  const existingPhones = new Set<string>();
  const existingEmails = new Set<string>();
  if (!supabase) return { existingPhones, existingEmails };

  // O banco guarda o telefone em formatos variados, então busca pelos 8 últimos dígitos (com qualquer
  // máscara no meio) e compara pela mesma chave, em vez de listar as formatações possíveis.
  const phoneKeys = Array.from(new Set(phones.map(phoneSuffixKey).filter(Boolean)));
  for (const part of chunk(phoneKeys, 25)) {
    const filter = part.map(k => `phone.like.${phoneLikePattern(k, '*')}`).join(',');
    const { data } = await supabase.from('leads').select('phone').eq('company_id', companyId).or(filter);
    (data || []).forEach(l => { const k = phoneSuffixKey(l.phone); if (k) existingPhones.add(k); });
  }

  const emailVariants = Array.from(new Set(emails.flatMap(e => [e, normalizeEmail(e)]).filter(Boolean)));
  for (const part of chunk(emailVariants, 150)) {
    const { data } = await supabase.from('leads').select('email').eq('company_id', companyId).in('email', part);
    (data || []).forEach(l => { const k = normalizeEmail(l.email); if (k) existingEmails.add(k); });
  }

  return { existingPhones, existingEmails };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function bulkInsertLeads(leads: any[]): Promise<any[]> {
  if (!supabase) return [];
  const { data, error } = await supabase.from('leads').insert(leads).select();
  if (error) throw leadInsertError(error);
  return data || [];
}

export async function findLeadForBulkUpdate(companyId: number, phone?: string, email?: string): Promise<{ id: number; name: string; notes: string | null } | null> {
  if (!supabase) return null;
  if (phone) {
    const found = await findLeadByPhoneSuffix(companyId, phone);
    if (found) return found;
  }
  const emailKey = normalizeEmail(email);
  if (emailKey) {
    const { data } = await supabase
      .from('leads')
      .select('id, name, notes')
      .eq('company_id', companyId)
      .in('email', Array.from(new Set([email as string, emailKey])))
      .limit(1)
      .maybeSingle();
    if (data) return data;
  }
  return null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function updateLeadForBulkImport(leadId: number, updateObj: any): Promise<any | null> {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('leads')
    .update(updateObj)
    .eq('id', leadId)
    .select()
    .single();
  if (error) return null;
  return data;
}
